// React Dependencies
import React, { useEffect, useState } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import { IconButton, makeStyles } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import DateFnsUtils from '@date-io/date-fns';
import moment from 'moment';

const useStyles = makeStyles({
  root: {
    '& .MuiInputBase-root': {
      padding: '0 !important',
      margin:  '0 !important',
    },
    '& .MuiOutlinedInput-input': {
      padding: '10.5px 14px !important',
    },
    '& .MuiOutlinedInput-adornedEnd': {
      paddingLeft:  0,
      paddingRight: 0,
      margin:       0,
    },
    '& .MuiInputAdornment-positionEnd': {
      marginRight: 4,
    },
    '& .MuiIconButton-root': {
      padding: 5,
    },
  },
});

const MaterialDatePicker = ({
  className,
  label,
  value,
  onChange,
  minDate,
  maxDate,
  variant,
  format,
  clearable = false,
  inputVariant,
  disabled,
  readOnly,
  style,
  inputStyle,
  disableToolbar,
  isRootStyleEnabled,
  disablePast,
  size,
  autoOk,
}) => {
  const classes = useStyles();

  const [selectedDate, setSelectedDate] = useState();

  useEffect(() => {
    setSelectedDate(value);
  }, [value]);

  const handleDateChange = (date) => {
    const momentDate = moment(date);

    if (momentDate.isValid()) {
      setSelectedDate(date);
      onChange(date);
    } else if (date === null) {
      setSelectedDate(null);
      onChange(null);
    } else {
      const fallbackDate = minDate || new Date();

      setSelectedDate(fallbackDate);
      onChange(fallbackDate);
    }
  };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <KeyboardDatePicker
        InputProps={{
          endAdornment: (
            <IconButton onClick={() => handleDateChange(null)} disabled={disabled}>
              <ClearIcon />
            </IconButton>
          ),
          style:      inputStyle,
          inputProps: {
            style: {
              fontSize: (inputStyle && inputStyle.fontSize) || undefined,
            },
          },
        }}
        InputAdornmentProps={{
          position: clearable ? 'start' : 'end',
        }}
        readOnly={readOnly}
        variant={variant}
        disabled={disabled}
        inputVariant={inputVariant}
        format={format}
        margin="normal"
        className={`material-date-picker ${isRootStyleEnabled ? classes.root : ''} ${className}`}
        label={label}
        value={selectedDate}
        onChange={handleDateChange}
        KeyboardButtonProps={{
          edge:         'end',
          size:         'small',
          'aria-label': 'change date',
        }}
        minDate={minDate}
        onKeyDown={(e) => e.preventDefault()}
        style={style}
        disableToolbar={disableToolbar}
        disablePast={disablePast}
        size={size}
        maxDate={maxDate}
        autoOk={autoOk}
      />
    </MuiPickersUtilsProvider>
  );
};

MaterialDatePicker.propTypes = {
  autoOk:    PropTypes.bool,
  className: PropTypes.string,
  label:     PropTypes.string,
  value:     PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape(),
  ]),
  onChange: PropTypes.func.isRequired,
  minDate:  PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.bool,
  ]),
  maxDate: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.bool,
  ]),
  format:             PropTypes.string,
  variant:            PropTypes.string,
  inputVariant:       PropTypes.string,
  disabled:           PropTypes.bool,
  disablePast:        PropTypes.bool,
  readOnly:           PropTypes.bool,
  clearable:          PropTypes.bool,
  inputStyle:         PropTypes.shape(Object),
  style:              PropTypes.shape(Object),
  disableToolbar:     PropTypes.bool,
  isRootStyleEnabled: PropTypes.bool,
  size:               PropTypes.oneOf(['small', 'medium', 'large']),
};

MaterialDatePicker.defaultProps = {
  autoOk:             false,
  className:          '',
  label:              '',
  format:             'dd/MM/yyyy',
  variant:            'inline',
  inputVariant:       'standard',
  disabled:           false,
  disablePast:        false,
  readOnly:           false,
  clearable:          false,
  value:              '' || {},
  minDate:            undefined,
  maxDate:            undefined,
  inputStyle:         {},
  style:              {},
  disableToolbar:     false,
  isRootStyleEnabled: false,
  size:               'medium',
};

export default MaterialDatePicker;
